import { useState } from "react";
import { KeyRound, Save, UserCog } from "lucide-react";
import { toast } from "sonner";
import { TerminalShell } from "@/components/TerminalShell";
import { PhoneField } from "@/components/PhoneField";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { userService } from "@/services/userService";
import { errorMessage } from "@/services/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const card = "rounded-md border border-[#141A26] bg-[#0A0E16] p-6";
const field = "h-11 rounded-md border-[#22252F] bg-[#0F1116] text-white";
const labelCls = "text-xs uppercase tracking-[0.2em] text-[#8A97AD]";

export default function SettingsPage() {
  const { t } = useLanguage();
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone_dial: user?.phone_dial || "+55",
    phone: user?.phone || "",
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    country: user?.country || "",
    city: user?.city || "",
    street: user?.street || "",
    postal_code: user?.postal_code || "",
  });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: profile.name };
      ["first_name", "last_name", "country", "city", "street", "postal_code"].forEach((f) => {
        payload[f] = profile[f];
      });
      if (profile.email) payload.email = profile.email;
      if (profile.phone) { payload.phone = profile.phone; payload.phone_dial = profile.phone_dial; }
      await userService.updateProfile(payload);
      await refresh();
      toast.success(t("settings.profileUpdated"));
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error(t("settings.pwdMismatch"));
      return;
    }
    try {
      await userService.changePassword(pwd.current_password, pwd.new_password);
      setPwd({ current_password: "", new_password: "", confirm: "" });
      toast.success(t("settings.pwdChanged"));
    } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <TerminalShell account={user?.name}>
      <div data-testid="settings-page" className="mx-auto max-w-3xl space-y-6 p-6">
        <div>
          <h1 className="font-heading text-2xl font-light tracking-tight text-white">{t("settings.title")}</h1>
          <p className="mt-1 text-sm text-[#8A97AD]">{t("settings.subtitle")}</p>
        </div>

        <section className={`${card} space-y-4`} data-testid="language-section">
          <p className="flex items-center gap-2 text-sm text-white">{t("settings.language")}</p>
          <LanguageSelector testId="settings-language-selector" />
        </section>

        <form onSubmit={saveProfile} className={`${card} space-y-4`} data-testid="profile-form">
          <p className="flex items-center gap-2 text-sm text-white"><UserCog className="h-4 w-4" /> {t("settings.profile")}</p>
          <div className="space-y-2">
            <Label className={labelCls}>{t("settings.name")}</Label>
            <Input required minLength={2} data-testid="profile-name-input" value={profile.name}
                   onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={field} />
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>{t("settings.email")}</Label>
            <Input type="email" data-testid="profile-email-input" value={profile.email}
                   onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                   placeholder="nome@dominio.com" className={field} />
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>{t("settings.phone")}</Label>
            <PhoneField dial={profile.phone_dial} phone={profile.phone}
                        onDial={(v) => setProfile({ ...profile, phone_dial: v })}
                        onPhone={(v) => setProfile({ ...profile, phone: v })}
                        testIdPrefix="profile-phone" />
          </div>

          <div className="space-y-4 border-t border-[#141A26] pt-4" data-testid="personal-info-section">
            <p className="text-sm text-white">{t("profile.personalInfo")}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className={labelCls}>{t("profile.firstName")}</Label>
                <Input data-testid="profile-first-name-input" value={profile.first_name}
                       onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} className={field} />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>{t("profile.lastName")}</Label>
                <Input data-testid="profile-last-name-input" value={profile.last_name}
                       onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} className={field} />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>{t("profile.country")}</Label>
                <Input data-testid="profile-country-input" value={profile.country}
                       onChange={(e) => setProfile({ ...profile, country: e.target.value })} className={field} />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>{t("profile.city")}</Label>
                <Input data-testid="profile-city-input" value={profile.city}
                       onChange={(e) => setProfile({ ...profile, city: e.target.value })} className={field} />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>{t("profile.street")}</Label>
                <Input data-testid="profile-street-input" value={profile.street}
                       onChange={(e) => setProfile({ ...profile, street: e.target.value })} className={field} />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>{t("profile.postalCode")}</Label>
                <Input data-testid="profile-postal-code-input" value={profile.postal_code}
                       onChange={(e) => setProfile({ ...profile, postal_code: e.target.value })} className={field} />
              </div>
            </div>
          </div>
          <Button type="submit" data-testid="save-profile-button"
                  className="h-11 rounded-md bg-[#2563EB] text-xs uppercase tracking-[0.15em] hover:bg-[#1D4ED8]">
            <Save className="mr-2 h-4 w-4" /> {t("settings.saveProfile")}
          </Button>
        </form>

        <form onSubmit={savePassword} className={`${card} space-y-4`} data-testid="password-form">
          <p className="flex items-center gap-2 text-sm text-white"><KeyRound className="h-4 w-4" /> {t("settings.security")}</p>
          <div className="space-y-2">
            <Label className={labelCls}>{t("settings.currentPwd")}</Label>
            <Input required type="password" data-testid="current-password-input" value={pwd.current_password}
                   onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className={field} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className={labelCls}>{t("settings.newPwd")}</Label>
              <Input required type="password" minLength={6} data-testid="new-password-input" value={pwd.new_password}
                     onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className={field} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>{t("settings.confirmPwd")}</Label>
              <Input required type="password" minLength={6} data-testid="confirm-password-input" value={pwd.confirm}
                     onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className={field} />
            </div>
          </div>
          <Button type="submit" data-testid="save-password-button"
                  className="h-11 rounded-md bg-[#10B981] text-xs uppercase tracking-[0.15em] text-[#04150F] hover:bg-[#0EA271]">
            {t("settings.changePwd")}
          </Button>
        </form>
      </div>
    </TerminalShell>
  );
}
